import {
  Box,
  Button,
  Text as ChakraText,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useQuery } from 'urql';

import type { ApiKeyEndpoint } from '@revelio/data-access';
import { FormField, Input } from '@revelio/ui';

import { AvailableApiKeyEndpointsQuery } from './userOperations';

export type RequestsPerSecondValue = Partial<Record<ApiKeyEndpoint, string>>;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  value: RequestsPerSecondValue;
  onChange: (value: RequestsPerSecondValue) => void;
  monthlyValue: string;
  onMonthlyChange: (value: string) => void;
  isReadOnly?: boolean;
};

const humanizeEndpoint = (endpoint: string) =>
  endpoint
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const parseRequestsPerSecond = (
  raw: string | null | undefined
): RequestsPerSecondValue => {
  if (!raw) return {};

  try {
    const parsed: unknown = JSON.parse(raw);

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }

    const result: RequestsPerSecondValue = {};
    for (const [key, val] of Object.entries(
      parsed as Record<string, unknown>
    )) {
      if (val === null || val === undefined) continue;
      if (typeof val === 'number' && Number.isFinite(val)) {
        result[key as ApiKeyEndpoint] = String(val);
      } else if (typeof val === 'string' && val.trim() !== '') {
        result[key as ApiKeyEndpoint] = val;
      }
    }
    return result;
  } catch {
    return {};
  }
};

export const serializeRequestsPerSecond = (
  value: RequestsPerSecondValue | undefined
): string | undefined => {
  if (!value) return undefined;

  const entries = Object.entries(value).filter(
    ([, v]) => v !== undefined && v !== null && String(v).trim() !== ''
  );

  if (entries.length === 0) return undefined;

  return JSON.stringify(
    Object.fromEntries(entries.map(([k, v]) => [k, Number(v)]))
  );
};

export const countConfiguredEndpoints = (value: RequestsPerSecondValue) =>
  Object.values(value).filter(
    (v) => v !== undefined && v !== null && String(v).trim() !== ''
  ).length;

export const RequestsPerSecondModal = ({
  isOpen,
  onClose,
  value,
  onChange,
  monthlyValue,
  onMonthlyChange,
  isReadOnly,
}: Props) => {
  const [{ data, fetching }] = useQuery({
    query: AvailableApiKeyEndpointsQuery,
    pause: !isOpen,
  });

  const [workingPerSecond, setWorkingPerSecond] =
    useState<RequestsPerSecondValue>(value);
  const [workingMonthly, setWorkingMonthly] = useState<string>(monthlyValue);

  useEffect(() => {
    if (isOpen) {
      setWorkingPerSecond(value);
      setWorkingMonthly(monthlyValue);
    }
  }, [isOpen, value, monthlyValue]);

  const endpoints = data?.availableApiKeyEndpoints ?? [];
  const isLoadingEndpoints = fetching && endpoints.length === 0;

  const onSave = () => {
    onChange(workingPerSecond);
    onMonthlyChange(workingMonthly);
    onClose();
  };

  const onPerSecondInputChange = (endpoint: ApiKeyEndpoint, next: string) => {
    setWorkingPerSecond((prev) => ({ ...prev, [endpoint]: next }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent maxHeight="85vh" margin="5">
        <ModalHeader>
          <ChakraText fontSize="lg" fontWeight="semibold">
            API Rate Limits
          </ChakraText>
          <ChakraText fontSize="sm" fontWeight="normal" color="gray.600">
            Defaults to 10 requests/sec per endpoint when left blank. Leave
            monthly blank for no cap.
          </ChakraText>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto">
          <Stack spacing="4">
            <Box mb={2}>
              <ChakraText fontWeight="semibold" color="text.primary" mb="2">
                General Limits
              </ChakraText>
              <FormField label="Requests Per Month">
                <Input
                  type="number"
                  min={0}
                  placeholder="Default monthly cap"
                  disabled={isReadOnly}
                  value={workingMonthly}
                  onChange={(event) => setWorkingMonthly(event.target.value)}
                />
              </FormField>
            </Box>

            <Box>
              <ChakraText fontWeight="semibold" color="text.primary" mb="2">
                Endpoint Limits (per second)
              </ChakraText>
              {isLoadingEndpoints ? (
                <Stack direction="row" align="center" spacing="2">
                  <Spinner size="sm" />
                  <ChakraText fontSize="sm">Loading endpoints...</ChakraText>
                </Stack>
              ) : (
                <Stack spacing="3">
                  {endpoints.map((endpoint) => (
                    <Box key={endpoint}>
                      <FormField label={humanizeEndpoint(endpoint)}>
                        <Input
                          type="number"
                          min={0}
                          placeholder="10"
                          disabled={isReadOnly}
                          value={workingPerSecond[endpoint] ?? ''}
                          onChange={(event) =>
                            onPerSecondInputChange(endpoint, event.target.value)
                          }
                        />
                      </FormField>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="green"
            onClick={onSave}
            isDisabled={isReadOnly || isLoadingEndpoints}
            isLoading={isLoadingEndpoints}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
